/**
 * buddyEngine — 确定性外观生成（Bones 骨架层）
 * FNV-1a 哈希 → Mulberry32 播种；相同 seedInput → 相同外观/属性。
 */
import {
  chance,
  fnv1a,
  mulberry32,
  pick,
  pickWeightedDistinct,
  randInt,
} from "@/lib/rng";

// 基元再导出：外部可从 buddyEngine 或 rng 任一处引入
export { fnv1a, mulberry32 };

/**
 * 伴侣动画状态（与对话状态机共享的约定类型）
 * 由 App / useBuddyDialogue 驱动，BuddyXin 按此切换姿势。
 */
export type BuddyAnim =
  | "idle"        // 默认待机
  | "drop"        // 入场从天而降
  | "pet"         // 摸头（脸红爱心/叉腰）
  | "angry"       // 连续戳弄发怒
  | "sleep"       // 闲置打瞌睡
  | "hold-shield" // 悬停神枢：掏对讲机/举警戒盾
  | "hold-chest"  // 悬停钱柜：高举黄金宝箱
  // ── 九态拟人状态（buddyStates 状态机驱动，见 @/lib/buddyStates）──
  | "calm"        // 平静：呼吸浮动 + 紫晕环
  | "joy"         // 喜悦：上跳 + 笑眼 + 星火
  | "focus"       // 专注：眯眼 + 聚气括弧
  | "alert"       // 警觉：圆睁 + 雷达波纹
  | "rage"        // 愤怒：狂抖 + 双怒气符
  | "fear"        // 恐惧：战栗 + 冷汗/惊恐锯齿
  | "fatigue"     // 疲惫：半垂眼 + 塌肩慢浮
  | "sorrow"      // 悲伤：垂泪 + 头顶雨云
  | "trance";     // 恍惚：螺旋瞳 + 悬浮环绕

export type Species = "robot" | "ghost" | "neko";

export type Accessory = "goggles" | "antenna" | "halo" | "duck-hat";

/** 配饰稀有度梯队 */
export type Rarity = "common" | "fine" | "rare" | "legendary";

export interface BuddyTraits {
  seed: number;
  seedLabel: string;
  species: Species;
  accessories: Accessory[];
  shiny: boolean;
  /** 五维属性 0-100 */
  stats: {
    DEBUG: number;
    ORDER: number;
    VAULT: number;
    SYNC: number;
    SNARK: number;
  };
  bodyColor: string;
  accentColor: string;
}

const SPECIES: Species[] = ["robot", "ghost", "neko"];

/**
 * 配饰稀有度表：护目镜（普通）/ 头顶天线（精良）/ 金色法环（稀有）/ 小鸭子帽（传说）
 * weight 决定抽取权重，越稀有权重越低。
 */
export const ACCESSORY_META: Record<
  Accessory,
  { label: string; rarity: Rarity; weight: number }
> = {
  goggles: { label: "护目镜", rarity: "common", weight: 40 },
  antenna: { label: "头顶天线", rarity: "fine", weight: 30 },
  halo: { label: "金色法环", rarity: "rare", weight: 20 },
  "duck-hat": { label: "小鸭子帽", rarity: "legendary", weight: 10 },
};

const ACCESSORY_POOL: Accessory[] = ["goggles", "antenna", "halo", "duck-hat"];

// 最高要求：禁一切荧光绿。批准色板：钛白/冷灰/冰青/全息青/极光紫/琥珀金/橙红（+工单明令的粉色爱心系）
const BODY_COLORS = [
  "#38bdf8", // 冰青
  "#8b5cf6", // 极光紫
  "#ff9fd0", // 樱粉（本体肤色，与摸头爱心同系）
  "#ffe066", // 金
  "#ff7ad9", // 霓虹粉
  "#f1f5f9", // 钛合金白
];
// 强调色会用于配饰（天线/护目镜/LED）→ 严格限定批准色板
const ACCENT_COLORS = [
  "#00f0ff", // 全息冷青
  "#8b5cf6", // 极光紫
  "#f1f5f9", // 钛合金白
  "#94a3b8", // 冷灰
  "#fbbf24", // 琥珀金
  "#ff9f5a", // 橙
];

/** 1% 独立闪光判定 */
const SHINY_RATE = 0.01;

export function rollBuddy(seedInput: string): BuddyTraits {
  const label = seedInput.trim() || "did:xin:anonymous";
  const seed = fnv1a(label);
  const rnd = mulberry32(seed);

  const species = pick(rnd, SPECIES);

  // 配饰：1-2 件，按稀有度权重不放回抽取
  const accessoryCount = randInt(rnd, 1, 2);
  const picked = pickWeightedDistinct(
    rnd,
    ACCESSORY_POOL.map((item) => ({ item, weight: ACCESSORY_META[item].weight })),
    accessoryCount,
  );

  // 独立 1% 闪光判定
  const shiny = chance(rnd, SHINY_RATE);

  const stat = () => randInt(rnd, 0, 100);
  const stats = {
    DEBUG: stat(),
    ORDER: stat(),
    VAULT: stat(),
    SYNC: stat(),
    SNARK: stat(),
  };

  const bodyColor = pick(rnd, BODY_COLORS);
  const accentColor = pick(rnd, ACCENT_COLORS);

  return {
    seed,
    seedLabel: label,
    species,
    accessories: picked,
    shiny,
    stats,
    bodyColor,
    accentColor,
  };
}

export const STAT_META: { key: keyof BuddyTraits["stats"]; label: string; note: string }[] = [
  { key: "DEBUG", label: "DEBUG", note: "算力感知 / 调试力" },
  { key: "ORDER", label: "ORDER", note: "戒律屏障 / 权限把关" },
  { key: "VAULT", label: "VAULT", note: "金库丰度 / 资产储备" },
  { key: "SYNC", label: "SYNC", note: "灵动共鸣 / 跨端中继" },
  { key: "SNARK", label: "SNARK", note: "灵性吐槽 / 毒舌度" },
];

export const SPECIES_LABEL: Record<Species, string> = {
  robot: "赛博灵机",
  ghost: "小幽灵",
  neko: "机灵猫",
};

/** 完整展示名（含稀有度后缀），与工单命名一致 */
export const ACCESSORY_LABEL: Record<Accessory, string> = {
  goggles: "护目镜",
  antenna: "头顶天线（精良）",
  halo: "金色法环（稀有）",
  "duck-hat": "小鸭子帽（传说）",
};

export const RARITY_LABEL: Record<Rarity, string> = {
  common: "普通",
  fine: "精良",
  rare: "稀有",
  legendary: "传说",
};
